// SmartLocks.jsx

// SmartLocks.jsx

import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Container,
  Paper,
  Stack,
  List,
  ListItem,
  Link as MuiLink,
  Divider,
} from '@mui/material';
import { ShoppingCart, ArrowForward } from '@mui/icons-material';
import Header from '../header/Header';
import products from './products.json';

// -- CONSTANTS AND HELPER ARRAYS --
const HERO_GRADIENT = 'linear-gradient(135deg, #e3ffe6 0%, #fff8e3 100%)';
const SECTION_PADDING = { xs: 3, sm: 5 };

const benefitList = [
  'Replaces the cost of rekeying at every tenant turn',
  'Reduces drive time and gas emissions associated with delivering keys at move-in',
  'Allows rental managers to track hourly vendors instead of relying on the honor system',
  'Gives tenants enough value to pay for smart perks to cover expense of access management subscription and reimburse for some hardware costs',
  'Easily charge $5 per month for modem/WiFi router rental',
  'Know when tenants have taken possession or moved out without driving to the property',
  'Many insurance companies offer discounts with camera monitoring and motion-activated alerts',
  'Monitor vacant properties to help prevent vandalism or burglary',
  'Gives leasing agents the option to verify the person entering matches photo IDs that have been verified',
  'Tenants move in with WiFi on day one of their lease',
  'Property managers are no longer purchasing lockboxes or code boxes that need to be moved from property to property',
  'No keys to be copied, stolen, misplaced during showings, or jammed locks',
  'Full remote monitoring and access capability',
];

const internetLinks = [
  {
    href: 'https://www.xfinity.com/now/internet?CMP=KNC-43700081120722642-GOOGLE-697118360144-c-xfinity%20wifi%20no%20contract-e-NOW+Internet_BR_Exact_West&gad_source=1&gclid=Cj0KCQiA_NC9BhCkARIsABSnSTYBSRu2kf2UO-ZUD8LygSzIy9Vb3Qgo5IpKZFmkFhjExMLFXEJTuhIaAophEALw_wcB&gclsrc=aw.ds',
    label: 'Xfinity Month-to-Month Agreement starting at $30 per month',
  },
  {
    href: 'https://official.spectrum.com/sem/internet?o=go&cmp=SEMR_BR_G_BBMC_112806137225_20063012298&p1=8776945486&v=SEMBR&gad_source=1&gclid=Cj0KCQiA_NC9BhCkARIsABSnSTbDPVxDu-dzd5OoWHt0n0gW0i3QPhoAkQbSlpkgoZBiE7LDcLxrJW8aAnm7EALw_wcB&gclsrc=aw.ds',
    label: 'Spectrum Affordable Plans With No Contract',
  },
  {
    href: 'https://www.centurylink.com/internet/no-annual-contract.html',
    label: 'CenturyLink Plans With No Contract',
  },
];

const SmartLocks = () => (
  <>
    <Header />
    <Box sx={{ background: '#fafbfc', minHeight: '100vh', pb: 10 }}>
      {/* HERO SECTION */}
      <Paper
        elevation={0}
        sx={{
          background: HERO_GRADIENT,
          py: { xs: 8, md: 10 },
          mb: 5,
          textAlign: 'center',
          borderRadius: 4,
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            fontWeight="bold"
            sx={{
              mb: 2,
              fontSize: { xs: '2.1rem', md: '3rem' },
              maxWidth: 900,
              mx: 'auto',
              lineHeight: 1.18,
            }}
          >
            Smart Lock Hardware For Keyless Access Management & Secured Automated Rental Showings
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: 650, mx: 'auto', mb: 4 }}
          >
            Secure your property with the best smart locks, cameras, and WiFi solutions without the upfront cost.
          </Typography>
          <Button
            variant="contained"
            size="large"
            color="primary"
            endIcon={<ArrowForward />}
            href="#order-now"
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 3,
              fontWeight: 'bold',
              fontSize: '1.12rem',
              boxShadow: 2,
            }}
          >
            Order Now
          </Button>
        </Container>
      </Paper>

      <Container maxWidth="md">
        <Stack spacing={5}>
          {/* COMPATIBILITY SECTION */}
          <Paper elevation={2} sx={{ p: SECTION_PADDING }}>
            <Typography variant="h4" fontWeight="bold" mb={2}>
              Smart Lock Compatibility
            </Typography>
            <Typography variant="body1" mb={1}>
              Our integration works best with Schlage Encode and Kwikset WiFi-enabled smart locks, but we are compatible with many other brands. The most common smart locks used in property management are Yale, August, Schlage, and Kwikset.
            </Typography>
            <Typography variant="body1" mb={1}>
              If your existing smart lock type is already on the list and has WiFi built-in, or you have a connected hub to enable WiFi, then skip ordering a smart lock from this page.
            </Typography>
            <Typography variant="body1" mb={1}>
              Lever smart locks are a better use than deadbolt locks for going keyless if door knobs still require keys, as tenants may still lock themselves out and you’re stuck with chasing keys for access.
            </Typography>
            <Typography variant="body1">
              If your deadbolt smart lock includes just a pull knob/handle without keys, is on our brand list above, and is WiFi-enabled, then you can also skip ordering a new smart lock to stay keyless and still be able to connect to our software.
            </Typography>
          </Paper>

          {/* DEVICE REQUIREMENTS */}
          <Paper elevation={2} sx={{ p: SECTION_PADDING }}>
            <Typography variant="h4" fontWeight="bold" mb={2}>
              Smart Lock & Device Requirements
            </Typography>
            <List>
              <ListItem>✅ Smart Lock with built-in WiFi or a hub for connectivity.</ListItem>
              <ListItem>✅ Doorbell camera and chime device (wired or battery-operated).</ListItem>
              <ListItem>✅ WiFi Modem/Router to keep smart locks and devices online.</ListItem>
              <ListItem>✅ Digital locks for backup access in case of power outages.</ListItem>
            </List>
            <Typography variant="body2" color="text.secondary" mt={2}>
              The minimum equipment required for showings and access management is a smart lock connected to WiFi using a brand that Tenant Guided Tours integrates with. We’ll provide all links for purchase options and installation instructions on this page below for easy reference. Additional devices enhance security and convenience.
            </Typography>
          </Paper>

          {/* PRODUCT GRID */}
          <Box>
            <Typography variant="h4" fontWeight="bold" mb={3} textAlign="center">
              Featured Products
            </Typography>
            <Grid container spacing={4} justifyContent="center">
              {products.map((product) => (
                <Grid item xs={12} sm={6} md={4} key={product.id}>
                  <Card
                    elevation={4}
                    sx={{
                      borderRadius: 3,
                      p: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      transition: 'transform 0.25s, box-shadow 0.25s',
                      '&:hover': {
                        transform: 'translateY(-4px) scale(1.025)',
                        boxShadow: 8,
                      },
                    }}
                  >
                    <CardMedia
                      component="img"
                      image={product.image}
                      alt={product.title}
                      sx={{
                        height: 220,
                        width: '100%',
                        objectFit: 'contain',
                        my: 2,
                      }}
                    />
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {product.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {product.description}
                      </Typography>
                    </CardContent>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<ShoppingCart />}
                      href={product.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        mb: 2,
                        fontWeight: 'bold',
                        letterSpacing: 0.5,
                        borderRadius: 2,
                        px: 4,
                      }}
                    >
                      Buy Now
                    </Button>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* BENEFITS SECTION */}
          <Paper elevation={2} sx={{ p: SECTION_PADDING }}>
            <Typography variant="h4" fontWeight="bold" mb={2}>
              Benefits of Purchasing Full Hardware Package
            </Typography>
            <List>
              {benefitList.map((item, idx) => (
                <ListItem key={idx}>{item}</ListItem>
              ))}
            </List>
          </Paper>

          {/* FINANCING & COSTS */}
          <Paper elevation={2} sx={{ p: SECTION_PADDING }}>
            <Typography variant="h4" fontWeight="bold" mb={2}>
              Financing & Costs
            </Typography>
            <Typography variant="body1" mb={2}>
              🔗 Some property management companies elect to purchase and finance equipment in-house to add another income stream and added convenience for their landlord clients/customers.
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h5" fontWeight="bold" mb={1}>
              Financing Options
            </Typography>
            <Typography variant="body1">
              Amazon’s Affirm option at checkout approves property owners for smart equipment for limits up to about $20,000 with instant approvals and no hard credit pulls. Term options from six months to eighteen months. ($18-$33 per month on average for locks, cameras, modem, etc.)
              <br /><br />
              For larger portfolios requiring more, please contact Tenant Guided Tours for enterprise smart equipment financing that gets property owners full smart packages with smart locks, cameras, modem and more for just $25 per month/per door with no upfront costs.
              <br /><br />
              🔗 Third-party investors have an opportunity to finance smart lock packages for a return of 50% ROI in three years, agreements are made with rental property owners directly, making it a win-win.
              <br /><br />
              🔗 Property owners have options besides a lump sum per door.
            </Typography>
          </Paper>

          {/* ORDER & SETUP GUIDE */}
          <Paper elevation={2} sx={{ p: SECTION_PADDING }}>
            <Typography variant="h4" fontWeight="bold" mb={2}>
              How to Order & Set Up
            </Typography>
            <List>
              <ListItem>
                <strong>Step 1: Select Equipment</strong>
                <br />
                Select the desired equipment from the product list above and determine the quantity needed. You can order directly from Amazon using the provided links. All lease-to-own installments are net 30 billing—just like access management and showing service orders. No upfront costs required to get started.
              </ListItem>
              <ListItem sx={{ display: 'block' }}>
                <strong>Step 2: Set Up Internet Service</strong>
                <br />
                Set up internet service with a provider of your choice. You won’t need any equipment if you already have a WiFi router and modem. Internet service can be month-to-month with no long-term contract, and costs can be reimbursed by the tenant with rent billing. Options include:
                {/* HORIZONTAL STACK OF INTERNET LINKS */}
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  mt={2}
                  mb={2}
                  alignItems="stretch"
                >
                  {internetLinks.map((link, idx) => (
                    <Card
                      key={idx}
                      elevation={3}
                      sx={{
                        flex: 1,
                        minWidth: 220,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        py: 2,
                        px: 2,
                        borderRadius: 2,
                        transition: 'box-shadow 0.2s, transform 0.2s',
                        '&:hover': {
                          boxShadow: 8,
                          transform: 'translateY(-2px) scale(1.035)',
                        },
                      }}
                    >
                      <MuiLink
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="none"
                        color="primary"
                        sx={{
                          fontWeight: 600,
                          fontSize: '1rem',
                          textAlign: 'center',
                          display: 'block',
                        }}
                      >
                        {link.label}
                      </MuiLink>
                    </Card>
                  ))}
                </Stack>
                <Divider sx={{ my: 1 }} />
              </ListItem>
              <ListItem>
                <strong>Step 3: Install Equipment</strong>
                <br />
                Installing equipment is easy and should only take about an hour and a half with instructions that we’ll provide. Equipment can be installed during maintenance visits or tenant turnovers to avoid separate trip charges. Your equipment is ordered from Amazon and will arrive at your property address, property manager’s office, or an Amazon secured locker near your rental property within three days.
              </ListItem>
              <ListItem>
                <strong>Step 4: Connect Your Smart Lock and Devices</strong>
                <br />
                Connecting your devices is easy once you’ve registered your equipment with each manufacturer’s user application software. Grant user access to installers for installation, then revoke it afterward—same with tenants. It’s super easy and takes just a few minutes when you’re familiar with our software.
              </ListItem>
            </List>
          </Paper>

          {/* INCLUDING HARDWARE COSTS */}
          <Paper elevation={2} sx={{ p: SECTION_PADDING }}>
            <Typography variant="h4" fontWeight="bold" mb={2}>
              Including Hardware Costs in Leases/Management Agreements
            </Typography>
            <Typography variant="body1">
              New leases with tenants or mutually amended leases may include offset credits from tenants as smart upgrades collected monthly on top of rent for the added costs for smart perks. Now tenants have homes with smart features that make their lives and living at your properties easier and more convenient.
              <br /><br />
            </Typography>
          </Paper>

          {/* ORDER NOW */}
          <Paper elevation={2} sx={{ p: SECTION_PADDING }} id="order-now">
            <Typography variant="h4" fontWeight="bold" mb={2}>
              Order Now
            </Typography>
            <Typography variant="body1" mb={2}>
              ✅ <strong>Order from Amazon</strong> directly.
              <br />
              ✅ <strong>Order with lease-to-own financing</strong> from Tenant Guided Tours.
              <br />
              🔗{' '}
              <MuiLink href="#" underline="hover" color="primary" sx={{ fontWeight: 500 }}>
                Order via Tenant Guided Tours
              </MuiLink>{' '}
              (Coming Soon)
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                color="primary"
                href="#"
                disabled
                sx={{ borderRadius: 2, fontWeight: 'bold', px: 4 }}
              >
                Order via Tenant Guided Tours (Coming Soon)
              </Button>
              <Button
                variant="outlined"
                color="success"
                href="https://amazon.com"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ borderRadius: 2, fontWeight: 'bold', px: 4 }}
                startIcon={<ShoppingCart />}
              >
                Order from Amazon
              </Button>
            </Stack>
          </Paper>
        </Stack>
      </Container>
    </Box>
  </>
);

export default SmartLocks;
